import { withTranslation } from '@application/i18n/with_translation';
import { ButtonArrange, Flex, Form } from '@cellula/react';
import type { AsyncSignalValue } from '@component/async.component';
import { TopBar } from '@component/top_bar.component';
import { DynamicForm } from '@feature/dynamic_form/dynamic_form.component';
import { FormWorker } from '@feature/dynamic_form/form_manager/form_worker';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router';
import { useBasket } from '../../hooks/use-basket';
import { ScreenError } from '../error';
import { useScreenForms } from './use-screen-forms';

export function ScreenForms() {
	const { basket } = useBasket();
	// Access-step routing is driven by basket.Participants, so wait until the basket has loaded.
	const isAccessDecisionReady = basket != null;

	const { forms, formWorker, loadSingleForm } = useScreenForms({ isAccessDecisionReady });

	if (!forms || forms.length === 0) {
		return null;
	}

	return (
		<>
			<TopBar.Content goBack />
			<Routes>
				<Route path=':formId/:formPage?' element={<SingleForm formWorker={formWorker} loadSingleForm={loadSingleForm} />} />
				<Route path='*' element={<Navigate to={`./${forms[0].Id}`} replace />} />
			</Routes>
		</>
	);
}

interface SingleFormProps {
	formWorker: AsyncSignalValue<Readonly<FormWorker>>;
	loadSingleForm: (formId: number) => Promise<void>;
}

function SingleForm({ formWorker, loadSingleForm }: SingleFormProps) {
	const { formId, formPage = 0 } = useParams();

	useEffect(
		function whenMount() {
			loadSingleForm(Number(formId));
		},
		[loadSingleForm, formId],
	);

	if (formWorker.flowState === 'error') {
		return <ScreenError />;
	}

	if (formWorker.flowState !== 'accessible') {
		return null;
	}

	return (
		<DynamicForm.Root formWorker={formWorker.value} defaultPagePosition={Number(formPage)}>
			<DynamicForm.Description />
			<DynamicForm.Content>
				<Flex grow={1} justify='flex-end'>
					<ButtonArrange>
						<Form.SubmitButton>{withTranslation('PW.Common.Next')}</Form.SubmitButton>
					</ButtonArrange>
				</Flex>
			</DynamicForm.Content>
		</DynamicForm.Root>
	);
}
